import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  LinearProgress,
  Paper,
  Slide,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
import { updateFileField } from "../../../ReduxStore/UserSlice";
import DocumentViewer from "../DocumentViewer/DocumentViewer";
import { showToast } from "../Util/Util";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DragDropFileUpload = ({
  name,
  value,
  onFileSelect,
  error,
  multiple = false,
  helperText,
  isOptional = false,
  disabled = false,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isOver, setIsOver] = useState(false);
  const [isRemoteFile, setIsRemoteFile] = useState(false);
  const [remoteFileUrl, setRemoteFileUrl] = useState("");
  const [remoteFileName, setRemoteFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    if (Array.isArray(value)) {
      setUploadedFile(value);
    } else if (
      value &&
      (value.remote === true || value.url || value.documentPath)
    ) {
      setIsRemoteFile(true);
      setRemoteFileUrl(value.url || value.documentPath || "");
      setRemoteFileName(
        value.filename ||
          value.name ||
          value.url?.split("/").pop() ||
          value.documentPath?.split("/").pop() ||
          "Document"
      );
      setUploadedFile(value);
    } else {
      setIsRemoteFile(false);
      setRemoteFileUrl("");
      setRemoteFileName("");
      setUploadedFile(value);
    }
  }, [value, name]);

  useEffect(() => {
    let timer = null;
    if (isUploading) {
      timer = setInterval(() => {
        setUploadProgress((prev) => {
          const newProgress = prev + Math.floor(Math.random() * 10);
          if (newProgress >= 100) {
            clearInterval(timer);
            setTimeout(() => {
              setIsUploading(false);
              setUploadProgress(0);
            }, 500);
            return 100;
          }
          return newProgress;
        });
      }, 200);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isUploading]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles?.length) {
        if (multiple) {
          const oversizedFiles = acceptedFiles.filter(
            (file) => file.size > 5 * 1024 * 1024
          );
          if (oversizedFiles.length > 0) {
            showToast("Some files exceed 5MB limit", "error");
            return;
          }
          const existingFiles = Array.isArray(uploadedFile) ? uploadedFile : [];
          const newFiles = [...existingFiles, ...acceptedFiles];
          setUploadedFile(newFiles);
          if (onFileSelect) {
            onFileSelect(newFiles);
            setIsRemoteFile(false);
            setRemoteFileUrl("");
            setRemoteFileName("");
          }
        } else {
          const file = acceptedFiles[0];
          if (file.size > 5 * 1024 * 1024) {
            showToast("File size exceeds 5MB limit", "error");
            return;
          }
          setUploadedFile(file);
          setIsUploading(true);
          if (onFileSelect) {
            if (isRemoteFile && (value?.documentId || value?.id)) {
              file.isReplacement = true;
              file.documentId = value.documentId || value.id;
            }
            onFileSelect(file);
            setIsRemoteFile(false);
            setRemoteFileUrl("");
            setRemoteFileName("");
          }
        }
      }
    },
    [multiple, onFileSelect, isRemoteFile, value, uploadedFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: disabled ? () => {} : onDrop,
    onDragEnter: disabled ? () => {} : () => setIsOver(true),
    onDragLeave: disabled ? () => {} : () => setIsOver(false),
    accept: {
      "image/*": [".png", ".gif", ".jpeg", ".jpg"],
      "application/pdf": [".pdf"],
    },
    multiple: multiple,
    disabled: disabled,
  });

  const handleRemoveFile = (index = null) => {
    if (multiple && Array.isArray(uploadedFile)) {
      const newFiles = [...uploadedFile];
      if (index !== null) {
        newFiles.splice(index, 1);
      }
      const filteredFiles = newFiles.filter((file) => file !== null);
      setUploadedFile(filteredFiles);
      if (onFileSelect) {
        onFileSelect(filteredFiles);
      }
      dispatch(
        updateFileField({
          field: name,
          value: filteredFiles.length > 0 ? filteredFiles : {},
        })
      );
    } else {
      setIsRemoteFile(false);
      setRemoteFileUrl("");
      setRemoteFileName("");

      dispatch(
        updateFileField({
          field: name,
          value: {},
        })
      );
      setUploadedFile(null);
      if (onFileSelect) {
        onFileSelect(null);
      }
    }
  };

  const handlePreviewDocument = () => {
    if (uploadedFile) {
      if (isRemoteFile) {
        let docType = "application/octet-stream"; // Default
        const url = remoteFileUrl.toLowerCase();

        if (url.match(/\.(jpe?g)$/i)) {
          docType = "image/jpeg";
        } else if (url.match(/\.(png)$/i)) {
          docType = "image/png";
        } else if (url.match(/\.(gif)$/i)) {
          docType = "image/gif";
        } else if (url.match(/\.(bmp)$/i)) {
          docType = "image/bmp";
        } else if (url.match(/\.(pdf)$/i)) {
          docType = "application/pdf";
        }

        if (
          url.includes("image") ||
          url.includes("jpg") ||
          url.includes("jpeg") ||
          url.includes("png") ||
          url.includes("gif")
        ) {
          docType = "image/jpeg";
        } else if (url.includes("pdf")) {
          docType = "application/pdf";
        }

        setPreviewFile({
          url: remoteFileUrl,
          name: remoteFileName,
          type: docType, // Add the determined type to the object
        });
      } else {
        setPreviewFile(uploadedFile);
      }
      setPreviewOpen(true);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
  };

  const handleDownload = () => {
    if (!uploadedFile) return;

    if (isRemoteFile && remoteFileUrl) {
      window.open(remoteFileUrl, "_blank");
    } else {
      const url = URL.createObjectURL(uploadedFile);
      const link = document.createElement("a");
      link.href = url;
      link.download = uploadedFile.name || "document";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    }
  };

  const getFileIcon = () => {
    if (isRemoteFile) {
      const url = remoteFileUrl.toLowerCase();
      if (url.endsWith(".pdf")) {
        return <PictureAsPdfIcon sx={{ fontSize: 40 }} />;
      } else if (url.match(/\.(jpe?g|png|gif|bmp)$/)) {
        return <ImageIcon sx={{ fontSize: 40 }} />;
      } else {
        return <DescriptionIcon sx={{ fontSize: 40 }} />;
      }
    } else if (uploadedFile) {
      if (uploadedFile.type?.startsWith("image/")) {
        return <ImageIcon sx={{ fontSize: 40 }} />;
      } else if (uploadedFile.type === "application/pdf") {
        return <PictureAsPdfIcon sx={{ fontSize: 40 }} />;
      } else {
        return <DescriptionIcon sx={{ fontSize: 40 }} />;
      }
    }
    return <DescriptionIcon sx={{ fontSize: 40 }} />;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Fade
        in={
          multiple
            ? true
            : (!uploadedFile ||
                (Array.isArray(uploadedFile) && !uploadedFile.length)) &&
              !isRemoteFile
        }
      >
        <Box
          {...getRootProps()}
          sx={{
            border: `1px solid ${
              error
                ? "#d32f2f"
                : disabled
                ? alpha(theme.palette.text.primary, 0.12)
                : isOver || isDragActive
                ? theme.palette.primary.main
                : alpha(theme.palette.text.primary, 0.12)
            }`,
            borderRadius: "8px",
            p: 4,
            textAlign: "center",
            cursor: disabled ? "not-allowed" : "pointer",
            backgroundColor: disabled
              ? alpha(theme.palette.text.primary, 0.04)
              : isOver || isDragActive
              ? alpha(theme.palette.primary.main, 0.04)
              : "#fff",
            transition: "all 0.3s ease",
            minHeight: 160,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            opacity: disabled ? 0.6 : 1,
            "&:hover": disabled ? {} : {
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon
            sx={{
              fontSize: 40,
              mb: 2,
              color:
                isOver || isDragActive
                  ? theme.palette.primary.main
                  : alpha(theme.palette.text.primary, 0.38),
              transition: "color 0.2s ease",
            }}
          />
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 400,
              letterSpacing: "0.01em",
              mb: 1,
            }}
          >
            {isOver || isDragActive
              ? "Release to upload"
              : multiple
              ? "Drag & drop files here"
              : "Drag & drop your file here"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 400,
              letterSpacing: "0.01em",
            }}
          >
            {isOptional ? "Optional • " : ""} PNG, JPG, PDF • Max 5MB
          </Typography>
        </Box>
      </Fade>

      {isUploading && (
        <Box sx={{ mt: 3, px: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Box sx={{ width: "100%", mr: 1 }}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 2, minWidth: 40 }}
            >
              {`${Math.round(uploadProgress)}%`}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Uploading {uploadedFile?.name || "file"}...
          </Typography>
        </Box>
      )}

      {(uploadedFile || isRemoteFile) &&
        !isUploading &&
        (multiple && Array.isArray(uploadedFile) ? (
          <Box sx={{ mt: 2 }}>
            {uploadedFile
              .filter((file) => file !== null)
              .map((file, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderRadius: "8px",
                    border: `1px solid ${
                      error
                        ? "#d32f2f"
                        : alpha(theme.palette.text.primary, 0.08)
                    }`,
                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.background.paper, 1),
                      borderColor: alpha(theme.palette.text.primary, 0.12),
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.08
                        ),
                        color: theme.palette.primary.main,
                      }}
                    >
                      {file && file.type?.startsWith("image/") ? (
                        <ImageIcon sx={{ fontSize: 24 }} />
                      ) : file && file.type === "application/pdf" ? (
                        <PictureAsPdfIcon sx={{ fontSize: 24 }} />
                      ) : (
                        <DescriptionIcon sx={{ fontSize: 24 }} />
                      )}
                    </Box>

                    <Box sx={{ ml: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: theme.palette.text.primary,
                          fontWeight: 500,
                          mb: 0.5,
                        }}
                      >
                        {file?.name || "Unknown file"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ letterSpacing: "0.01em" }}
                      >
                        {file instanceof File
                          ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                          : ""}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setPreviewFile(file);
                        setPreviewOpen(true);
                      }}
                      sx={{
                        color: theme.palette.text.secondary,
                        "&:hover": { color: theme.palette.primary.main },
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      sx={{
                        color: theme.palette.text.secondary,
                        "&:hover": { color: theme.palette.error.main },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mt: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: "8px",
              border: `1px solid ${
                error ? "#d32f2f" : alpha(theme.palette.text.primary, 0.08)
              }`,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: alpha(theme.palette.background.paper, 1),
                borderColor: alpha(theme.palette.text.primary, 0.12),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                }}
              >
                {getFileIcon()}
              </Box>

              <Box sx={{ ml: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  {isRemoteFile
                    ? remoteFileName
                    : uploadedFile?.name || "Unknown file"}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ letterSpacing: "0.01em" }}
                >
                  {isRemoteFile
                    ? "Uploaded document"
                    : uploadedFile instanceof File
                    ? `${(uploadedFile.size / 1024 / 1024).toFixed(2)} MB`
                    : ""}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                size="small"
                onClick={handlePreviewDocument}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": { color: theme.palette.primary.main },
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>

              {isRemoteFile && (
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  sx={{
                    color: theme.palette.text.secondary,
                    "&:hover": { color: theme.palette.primary.main },
                  }}
                >
                  <FileDownloadIcon fontSize="small" />
                </IconButton>
              )}

              <IconButton
                size="small"
                onClick={() => handleRemoveFile()}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": { color: theme.palette.error.main },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        ))}

      {error && helperText && (
        <Typography
          color="error"
          variant="caption"
          sx={{
            display: "block",
            mt: 1,
            ml: 1,
          }}
        >
          {helperText}
        </Typography>
      )}

      <Dialog
        open={previewOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            height: "85vh",
            maxHeight: "85vh",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${alpha(
              theme.palette.text.primary,
              0.08
            )}`,
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              fontSize: "1.125rem",
              color: theme.palette.text.primary,
            }}
          >
            {isRemoteFile
              ? remoteFileName
              : uploadedFile?.name || "Document Preview"}
          </Typography>
          <IconButton
            onClick={handleClosePreview}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                color: theme.palette.text.primary,
                backgroundColor: alpha(theme.palette.text.primary, 0.04),
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            height: "100%",
            bgcolor: alpha(theme.palette.background.paper, 0.96),
          }}
        >
          {isRemoteFile || uploadedFile ? (
            <Box sx={{ height: "100%", p: 0 }}>
              <Box
                sx={{
                  height: "100%",
                  overflow: "hidden",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  bgcolor: alpha(theme.palette.background.paper, 0.6),
                }}
              >
                <DocumentViewer
                  document={
                    previewFile ||
                    (Array.isArray(uploadedFile) ? uploadedFile[0] : uploadedFile)
                  }
                  documentType={
                    previewFile?.type === "application/pdf"
                      ? "application/pdf"
                      : previewFile?.type?.startsWith("image/") ||
                        (Array.isArray(uploadedFile)
                          ? uploadedFile[0]?.type?.startsWith("image/")
                          : uploadedFile?.type?.startsWith("image/"))
                      ? "image/jpeg"
                      : "application/pdf"
                  }
                  title={
                    previewFile?.name ||
                    (Array.isArray(uploadedFile)
                      ? uploadedFile[0]?.name
                      : uploadedFile?.name)
                  }
                  height="100%"
                  containerStyle={{
                    overflow: "hidden",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                />
              </Box>
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "space-between",
            p: 2,
            bgcolor: theme.palette.background.paper,
            borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "flex",
              alignItems: "center",
              "&::before": {
                content: '""',
                display: "inline-block",
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: theme.palette.success.main,
                marginRight: 1,
              },
            }}
          >
            Secure preview mode
          </Typography>
          <Button
            onClick={handleClosePreview}
            variant="text"
            size="small"
            sx={{
              textTransform: "none",
              color: theme.palette.text.primary,
              "&:hover": {
                backgroundColor: alpha(theme.palette.text.primary, 0.04),
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DragDropFileUpload;
